import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  inject,
} from '@angular/core';

const MENU_GAP_PX = 4;
const VIEWPORT_PADDING_PX = 8;
const MENU_Z_INDEX = 9999;

/**
 * Positions row action menus with fixed coordinates so they escape overflow
 * containers (e.g. `.table-responsive`) and stay fully visible.
 */
@Directive({
  selector: '[appActionMenuPanel]',
  standalone: true,
})
export class ActionMenuPanelDirective implements AfterViewInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  @HostBinding('style.position') position = 'fixed';
  @HostBinding('style.zIndex') zIndex = String(MENU_Z_INDEX);
  @HostBinding('style.top.px') top = 0;
  @HostBinding('style.left.px') left = 0;

  ngAfterViewInit(): void {
    this.positionMenu();
    this.cdr.detectChanges();
  }

  private positionMenu(): void {
    const menu = this.host.nativeElement;
    const wrapper = menu.closest('.agent-action-menu');
    const trigger = wrapper?.querySelector('button') as HTMLElement | null;
    if (!trigger) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const menuHeight = menu.offsetHeight || menu.scrollHeight;
    const menuWidth = menu.offsetWidth || menu.scrollWidth || 208;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow =
      viewportHeight - triggerRect.bottom - MENU_GAP_PX - VIEWPORT_PADDING_PX;
    const spaceAbove = triggerRect.top - MENU_GAP_PX - VIEWPORT_PADDING_PX;
    const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    let top: number;
    if (openUpward) {
      top = triggerRect.top - menuHeight - MENU_GAP_PX;
      if (top < VIEWPORT_PADDING_PX) {
        top = VIEWPORT_PADDING_PX;
        menu.style.maxHeight = `${Math.max(120, triggerRect.top - MENU_GAP_PX - VIEWPORT_PADDING_PX)}px`;
        menu.style.overflowY = 'auto';
      }
    } else {
      top = triggerRect.bottom + MENU_GAP_PX;
      const maxTop = viewportHeight - menuHeight - VIEWPORT_PADDING_PX;
      if (top > maxTop) {
        top = Math.max(VIEWPORT_PADDING_PX, maxTop);
        menu.style.maxHeight = `${viewportHeight - top - VIEWPORT_PADDING_PX}px`;
        menu.style.overflowY = 'auto';
      }
    }

    let left = triggerRect.right - menuWidth;
    if (left < VIEWPORT_PADDING_PX) {
      left = VIEWPORT_PADDING_PX;
    }
    if (left + menuWidth > viewportWidth - VIEWPORT_PADDING_PX) {
      left = viewportWidth - menuWidth - VIEWPORT_PADDING_PX;
    }

    this.top = top;
    this.left = left;
  }
}
